import Component from '@glimmer/component';
import { service } from '@ember/service';
import t from 'ember-intl/helpers/t';
import type { IntlService } from 'ember-intl';
import { Section } from 'tio-ui/components/layout';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import type RecommendationModel from 'tio-data/models/recommendation';
import {
  parseResultSections,
  type SortPlansPayload,
} from 'tio-employee/utils/strategy-finder-0726';

interface InputsSummarySignature {
  Args: {
    recommendation: RecommendationModel;
  };
  Element: HTMLElement;
}

export default class StrategyFinder0726InputsSummary extends Component<InputsSummarySignature> {
  @service declare intl: IntlService;

  get sections() {
    return parseResultSections(
      this.args.recommendation.calculationInput,
      this.args.recommendation.calculationOutput
    );
  }

  get shared(): SortPlansPayload | null {
    return this.sections[0]?.input ?? null;
  }

  dollars = (cents: number | null | undefined) => (cents ?? 0) / 100;

  loanTypeLabel = (loanType: string) => {
    const key = `strategy_finder_0726.loan_types.${loanType}`;
    return this.intl.exists(key) ? this.intl.t(key) : loanType;
  };

  planLabel = (planKey: string | null) => {
    if (!planKey) {
      return this.intl.t('strategy_finder_0726.loan_no_current_plan');
    }
    const key = `strategy_finder_0726.plans.${planKey}`;
    return this.intl.exists(key) ? this.intl.t(key) : planKey;
  };

  get payoffGoalLabel() {
    const goal = this.shared?.payoff_goal;
    if (goal === 'quick') {
      return this.intl.t('strategy_finder_0726.payoff_quickly');
    }
    if (goal === 'slow') {
      return this.intl.t('strategy_finder_0726.lower_monthly');
    }
    return '—';
  }

  get affordLabel() {
    const afford = this.shared?.afford_payments;
    if (afford === 'yes') {
      return this.intl.t('common.yes');
    }
    if (afford === 'no') {
      return this.intl.t('common.no');
    }
    return '—';
  }

  get firstLoanTimeLabel() {
    const value = this.shared?.first_loan_time;
    const key = `strategy_finder_0726.first_loan_time_options.${value}`;
    return value && this.intl.exists(key) ? this.intl.t(key) : '—';
  }

  get lastLoanTimeLabel() {
    const value = this.shared?.last_loan_time;
    const key = `strategy_finder_0726.last_loan_time_options.${value}`;
    return value && this.intl.exists(key) ? this.intl.t(key) : '—';
  }

  // Loan-forgiveness answers, shown back exactly as the borrower entered them.
  get workingTowardLabel() {
    const value = this.shared?.currently_working_toward;
    if (value === 'yes') {
      return this.intl.t('common.yes');
    }
    if (value === 'no-and-not-interested') {
      return this.intl.t('strategy_finder_0726.forgiveness.not_interested');
    }
    if (value === 'no-but-want-learn') {
      return this.intl.t('strategy_finder_0726.forgiveness.not_yet');
    }
    return '—';
  }

  get programLabel() {
    const value = this.shared?.forgiveness_program;
    const key = `strategy_finder_0726.forgiveness_program.${value}`;
    return value && this.intl.exists(key) ? this.intl.t(key) : '—';
  }

  get isGov() {
    return (this.shared?.employment_type ?? '').includes('government-tribal-nonprofit');
  }

  get isTeacher() {
    return (this.shared?.employment_type ?? '').includes('teacher');
  }

  get employmentLabel() {
    const type = this.shared?.employment_type ?? '';
    const parts: string[] = [];
    if (this.isGov) {
      parts.push(this.intl.t('strategy_finder_0726.forgiveness.gov_label'));
    }
    if (this.isTeacher) {
      parts.push(this.intl.t('strategy_finder_0726.forgiveness.teacher_label'));
    }
    if (type === 'other-private-sector') {
      parts.push(this.intl.t('strategy_finder_0726.forgiveness.other_label'));
    }
    return parts.length ? parts.join(', ') : '—';
  }

  get planInGovLabel() {
    const value = this.shared?.plan_in_gov;
    if (value === 'for-10-years-more') {
      return this.intl.t('strategy_finder_0726.forgiveness.at_least_ten');
    }
    if (value === 'not-for-10-years') {
      return this.intl.t('strategy_finder_0726.forgiveness.ten_years_unlikely');
    }
    return '—';
  }

  get planAsTeacherLabel() {
    const value = this.shared?.plan_as_teacher;
    if (value === 'for-5-years-and-more') {
      return this.intl.t('strategy_finder_0726.forgiveness.teacher_five_years');
    }
    if (value === 'less-than-5-years') {
      return this.intl.t('strategy_finder_0726.forgiveness.teacher_five_years_unlikely');
    }
    return '—';
  }

  get tlf17500Label() {
    return this.shared?.tlf_qualifies_17500 ? this.intl.t('common.yes') : this.intl.t('common.no');
  }

  get sortByLabel() {
    return this.shared?.sort_by === 'total_then_first_month'
      ? this.intl.t('strategy_finder_0726.results.sorted_by_total')
      : this.intl.t('strategy_finder_0726.results.sorted_by_first_month');
  }

  <template>
    <Section data-test-sf-0726-inputs ...attributes>
      <:header>{{t "strategy_finder_0726.inputs.title"}}</:header>
      <:body>
        {{#if this.shared}}
          <dl class="grid gap-x-6 gap-y-3 md:grid-cols-2 text-sm">
            <div>
              <dt class="font-semibold">{{t "strategy_finder_0726.payoff_goal"}}</dt>
              <dd>{{this.payoffGoalLabel}}</dd>
            </div>
            <div>
              <dt class="font-semibold">{{t "strategy_finder_0726.afford_payments"}}</dt>
              <dd>{{this.affordLabel}}</dd>
            </div>
            <div>
              <dt class="font-semibold">{{t "strategy_finder_0726.agi"}}</dt>
              <dd>
                {{safeFormatNumber
                  (this.dollars this.shared.agi)
                  style="currency"
                  currency="USD"
                  maximumFractionDigits=0
                }}
              </dd>
            </div>
            <div>
              <dt class="font-semibold">{{t "strategy_finder_0726.adults"}}</dt>
              <dd>{{this.shared.number_of_adults}}</dd>
            </div>
            <div>
              <dt class="font-semibold">{{t "strategy_finder_0726.children"}}</dt>
              <dd>{{this.shared.number_of_children}}</dd>
            </div>
            <div>
              <dt class="font-semibold">{{t "strategy_finder_0726.first_loan_time"}}</dt>
              <dd>{{this.firstLoanTimeLabel}}</dd>
            </div>
            <div>
              <dt class="font-semibold">{{t "strategy_finder_0726.last_loan_time"}}</dt>
              <dd>{{this.lastLoanTimeLabel}}</dd>
            </div>
            <div>
              <dt class="font-semibold">{{t "strategy_finder_0726.paid_months"}}</dt>
              <dd>{{this.shared.current_paid_months}}</dd>
            </div>
            <div>
              <dt class="font-semibold">{{t "strategy_finder_0726.forgiveness.working_toward"}}</dt>
              <dd>{{this.workingTowardLabel}}</dd>
            </div>
            {{#if this.shared.forgiveness_program}}
              <div>
                <dt class="font-semibold">{{t
                    "strategy_finder_0726.forgiveness.which_program"
                  }}</dt>
                <dd>{{this.programLabel}}</dd>
              </div>
            {{/if}}
            {{#if this.shared.employment_type}}
              <div>
                <dt class="font-semibold">{{t
                    "strategy_finder_0726.forgiveness.employment_types"
                  }}</dt>
                <dd>{{this.employmentLabel}}</dd>
              </div>
            {{/if}}
            {{#if this.isGov}}
              <div>
                <dt class="font-semibold">{{t "strategy_finder_0726.forgiveness.gov_how_long"}}</dt>
                <dd>{{this.planInGovLabel}}</dd>
              </div>
            {{/if}}
            {{#if this.isTeacher}}
              <div>
                <dt class="font-semibold">{{t
                    "strategy_finder_0726.forgiveness.teacher_how_long"
                  }}</dt>
                <dd>{{this.planAsTeacherLabel}}</dd>
              </div>
              <div>
                <dt class="font-semibold">{{t "strategy_finder_0726.inputs.tlf_17500"}}</dt>
                <dd>{{this.tlf17500Label}}</dd>
              </div>
            {{/if}}
            <div>
              <dt class="font-semibold">{{t "strategy_finder_0726.residence_state"}}</dt>
              <dd>{{this.shared.residence_state}}</dd>
            </div>
            <div>
              <dt class="font-semibold">{{t "strategy_finder_0726.inputs.sort_by"}}</dt>
              <dd>{{this.sortByLabel}}</dd>
            </div>
          </dl>

          <h3 class="tio-text-sm-semibold mt-4 mb-2">{{t "strategy_finder_0726.loans_heading"}}</h3>
          <div class="grid gap-3 md:grid-cols-2">
            {{#each this.sections as |section|}}
              {{#if section.input}}
                <div
                  class="tio-bg-surface border tio-border-default rounded-lg p-3 text-sm"
                  data-test-sf-0726-inputs-loan={{section.loanType}}
                >
                  <h4 class="tio-text-sm-semibold pb-1">{{this.loanTypeLabel section.loanType}}</h4>
                  <dl>
                    <div class="tio-row justify-between">
                      <dt>{{t "strategy_finder_0726.loan_current_balance"}}</dt>
                      <dd>
                        {{safeFormatNumber
                          (this.dollars section.input.current_balance)
                          style="currency"
                          currency="USD"
                          maximumFractionDigits=0
                        }}
                      </dd>
                    </div>
                    <div class="tio-row justify-between">
                      <dt>{{t "strategy_finder_0726.loan_interest_rate"}}</dt>
                      <dd>
                        {{safeFormatNumber
                          section.input.interest_rate_annual
                          style="percent"
                          maximumFractionDigits=2
                        }}
                      </dd>
                    </div>
                    <div class="tio-row justify-between">
                      <dt>{{t "strategy_finder_0726.loan_monthly_payment"}}</dt>
                      <dd>
                        {{safeFormatNumber
                          (this.dollars section.enteredMonthlyPaymentCents)
                          style="currency"
                          currency="USD"
                          maximumFractionDigits=0
                        }}
                      </dd>
                    </div>
                    <div class="tio-row justify-between">
                      <dt>{{t "strategy_finder_0726.loan_current_plan"}}</dt>
                      <dd>{{this.planLabel section.currentPlan}}</dd>
                    </div>
                  </dl>
                </div>
              {{/if}}
            {{/each}}
          </div>
        {{/if}}
      </:body>
    </Section>
  </template>
}
